import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, CreditCard, Calendar, MessageSquare, Phone, ChevronRight, LogOut, Home, FileText, HelpCircle, Bell } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login Screen
const LoginScreen = ({ onLogin }) => {
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!mobile.trim()) {
      toast.error('Please enter your mobile number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/customer/verify-mobile`, { mobile });
      localStorage.setItem('subscriber_mobile', mobile);
      localStorage.setItem('subscriber_name', response.data.name);
      localStorage.setItem('subscriber_id', response.data.subscriber_id);
      onLogin(response.data);
      toast.success(`Welcome, ${response.data.name}!`);
    } catch (error) {
      toast.error('Mobile number not found. Please contact support.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex flex-col items-center justify-center p-6">
      <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-6 shadow-xl">
        <span className="text-blue-600 font-bold text-2xl">Mipay</span>
      </div>
      <h1 className="text-white text-2xl font-bold mb-2">Subscriber Portal</h1>
      <p className="text-blue-200 mb-8 text-center">Access your account, bills, and support</p>
      
      <form onSubmit={handleLogin} className="w-full max-w-sm">
        <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
          <label className="block text-white text-sm mb-2">Mobile Number</label>
          <div className="relative mb-4">
            <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-300" size={20} />
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="+639123456789"
              className="w-full bg-white/20 text-white placeholder-blue-200 rounded-xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-blue-600 font-bold py-4 rounded-xl hover:bg-blue-50 transition-colors disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Login'}
          </button>
        </div>
      </form>
      
      <p className="text-blue-200 text-sm mt-8">
        Use your registered mobile number to login
      </p>
    </div>
  );
};

// Home Tab
const HomeTab = ({ subscriber }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriberData();
  }, []);

  const fetchSubscriberData = async () => {
    try {
      const mobile = localStorage.getItem('subscriber_mobile');
      const response = await axios.get(`${API}/customer/my-account/${encodeURIComponent(mobile)}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24">
      {/* Welcome Card */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white mb-6">
        <p className="text-blue-200 text-sm">Welcome back,</p>
        <h2 className="text-2xl font-bold mb-4">{subscriber?.name || 'Subscriber'}</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-200 text-xs">Current Plan</p>
            <p className="font-semibold">{stats?.plan || 'Standard'}</p>
          </div>
          <div className="text-right">
            <p className="text-blue-200 text-xs">Monthly Fee</p>
            <p className="font-bold text-xl">₱{stats?.amount?.toLocaleString() || '0'}</p>
          </div>
        </div>
      </div>

      {/* Status Card */}
      <div className={`rounded-2xl p-4 mb-6 ${stats?.status === 'active' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600">Account Status</p>
            <p className={`font-bold text-lg ${stats?.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
              {stats?.status?.toUpperCase() || 'UNKNOWN'}
            </p>
          </div>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${stats?.status === 'active' ? 'bg-green-100' : 'bg-red-100'}`}>
            <div className={`w-4 h-4 rounded-full ${stats?.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
          <Calendar className="text-blue-600 mb-2" size={24} />
          <p className="text-xs text-slate-500">Next Due Date</p>
          <p className="font-bold text-slate-900">
            {stats?.due_date ? new Date(stats.due_date).toLocaleDateString() : 'N/A'}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
          <CreditCard className="text-green-600 mb-2" size={24} />
          <p className="text-xs text-slate-500">Last Payment</p>
          <p className="font-bold text-slate-900">
            {stats?.last_payment_date ? new Date(stats.last_payment_date).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      </div>

      {/* Account Details */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">Account Details</h3>
        </div>
        <div className="divide-y divide-slate-100">
          <div className="flex justify-between p-4">
            <span className="text-slate-500">Mobile Number</span>
            <span className="font-medium text-slate-900">{stats?.mobile || 'N/A'}</span>
          </div>
          <div className="flex justify-between p-4">
            <span className="text-slate-500">Email</span>
            <span className="font-medium text-slate-900">{stats?.email || 'N/A'}</span>
          </div>
          <div className="flex justify-between p-4">
            <span className="text-slate-500">Billing Cycle</span>
            <span className="font-medium text-slate-900 capitalize">{stats?.billing_cycle || 'Monthly'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Bills Tab
const BillsTab = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      const mobile = localStorage.getItem('subscriber_mobile');
      const response = await axios.get(`${API}/customer/my-bills/${encodeURIComponent(mobile)}`);
      setBills(response.data.bills || []);
    } catch (error) {
      console.error('Error fetching bills:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24">
      <h2 className="text-xl font-bold text-slate-900 mb-4">Bills & Invoices</h2>
      
      {bills.length > 0 ? (
        <div className="space-y-3">
          {bills.map((bill, index) => (
            <div key={index} className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-slate-900">₱{bill.amount?.toLocaleString()}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  bill.status === 'paid' ? 'bg-green-100 text-green-700' :
                  bill.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {bill.status?.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Due: {new Date(bill.due_date).toLocaleDateString()}</span>
                <span className="text-slate-500">#{bill.invoice_number}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500">
          <FileText size={48} className="mx-auto mb-3 text-slate-300" />
          <p>No bills found</p>
        </div>
      )}
    </div>
  );
};

// Payments Tab
const PaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const mobile = localStorage.getItem('subscriber_mobile');
      const response = await axios.get(`${API}/customer/my-payments/${encodeURIComponent(mobile)}`);
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24">
      <h2 className="text-xl font-bold text-slate-900 mb-4">Payment History</h2>
      
      {payments.length > 0 ? (
        <div className="space-y-3">
          {payments.map((payment, index) => (
            <div key={index} className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-green-600">₱{payment.amount?.toLocaleString()}</span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  PAID
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{new Date(payment.payment_date).toLocaleDateString()}</span>
                <span className="text-slate-500 capitalize">{payment.payment_method}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500">
          <CreditCard size={48} className="mx-auto mb-3 text-slate-300" />
          <p>No payment history</p>
        </div>
      )}
    </div>
  );
};

// Support Tab
const SupportTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ subject: '', message: '', priority: 'medium' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const mobile = localStorage.getItem('subscriber_mobile');
      const response = await axios.get(`${API}/customer/my-tickets/${encodeURIComponent(mobile)}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const mobile = localStorage.getItem('subscriber_mobile');
      await axios.post(`${API}/customer/submit-ticket`, {
        mobile,
        ...formData
      });
      toast.success('Support ticket submitted!');
      setShowForm(false);
      setFormData({ subject: '', message: '', priority: 'medium' });
      fetchTickets();
    } catch (error) {
      toast.error('Failed to submit ticket');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-900">Support</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-medium"
        >
          {showForm ? 'Cancel' : 'New Ticket'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm mb-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="e.g., No internet connection"
              required
              className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Describe your issue..."
              required
              rows="4"
              className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High - Urgent</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </form>
      )}

      {tickets.length > 0 ? (
        <div className="space-y-3">
          {tickets.map((ticket, index) => (
            <div key={index} className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-slate-900">{ticket.subject}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                  ticket.status === 'open' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {ticket.status?.toUpperCase()}
                </span>
              </div>
              <p className="text-sm text-slate-600 mb-2">{ticket.message}</p>
              <p className="text-xs text-slate-400">{new Date(ticket.created_at).toLocaleDateString()}</p>
              {ticket.response && (
                <div className="mt-3 p-3 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-xs font-medium text-green-800 mb-1">Response:</p>
                  <p className="text-sm text-green-700">{ticket.response}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500">
          <HelpCircle size={48} className="mx-auto mb-3 text-slate-300" />
          <p>No support tickets</p>
          <p className="text-sm mt-1">Create a ticket if you need help</p>
        </div>
      )}
    </div>
  );
};

// Main App Component
const SubscriberApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [subscriber, setSubscriber] = useState(null);
  const [activeTab, setActiveTab] = useState('home');

  useEffect(() => {
    // Check if already logged in
    const savedMobile = localStorage.getItem('subscriber_mobile');
    const savedName = localStorage.getItem('subscriber_name');
    if (savedMobile && savedName) {
      setIsLoggedIn(true);
      setSubscriber({ mobile: savedMobile, name: savedName });
    }
  }, []);

  const handleLogin = (data) => {
    setSubscriber(data);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('subscriber_mobile');
    localStorage.removeItem('subscriber_name');
    localStorage.removeItem('subscriber_id');
    setIsLoggedIn(false);
    setSubscriber(null);
    setActiveTab('home');
  };

  if (!isLoggedIn) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <LoginScreen onLogin={handleLogin} />
      </>
    );
  }

  const tabs = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'bills', label: 'Bills', icon: FileText },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'support', label: 'Support', icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-sm">M</span>
          </div>
          <div>
            <p className="font-bold text-slate-900">Mipay</p>
            <p className="text-xs text-slate-500">{subscriber?.name}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-red-500">
          <LogOut size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="pb-20">
        {activeTab === 'home' && <HomeTab subscriber={subscriber} />}
        {activeTab === 'bills' && <BillsTab />}
        {activeTab === 'payments' && <PaymentsTab />}
        {activeTab === 'support' && <SupportTab />}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 flex justify-around">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors ${
              activeTab === tab.id
                ? 'text-blue-600 bg-blue-50'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <tab.icon size={20} />
            <span className="text-xs mt-1 font-medium">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SubscriberApp;
